import React, { useState, useEffect } from 'react';
import ct from 'countries-and-timezones';
import { useData } from '../hooks/useData.js';
import { apiFetch, updateTenantBranding } from '../api/index.js';
import { LoadingSpinner, Button, FormInput, FormSelect } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import { generateSecondaryColor, generateSecondaryColorWithOffset } from '../utils/colorUtils.js';
import { FONT_FAMILIES } from '../utils/branding.ts';
import { notify } from '@/components/core';

// Simple Card Component
const ConfigCard = ({ title, icon, children, actions }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg text-primary">
                    <DynamicIcon name={icon} className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            </div>
            {actions}
        </div>
        <div className="p-6 space-y-4">
            {children}
        </div>
    </div>
);

const FeatureToggle = ({ label, checked, onChange, description }) => (
    <div className="flex items-center justify-between py-3 border-b dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 px-2 rounded -mx-2 transition-colors">
        <div>
            <p className="font-medium text-gray-900 dark:text-gray-100">{label}</p>
            {description && <p className="text-xs text-gray-500">{description}</p>}
        </div>
        <button
            onClick={onChange}
            type="button"
            className={`${checked ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-600'}
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
            role="switch"
            aria-checked={checked}
        >
            <span
                aria-hidden="true"
                className={`${checked ? 'translate-x-5' : 'translate-x-0'}
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
            />
        </button>
    </div>
);

const IANA_TIMEZONES = Object.values(ct.getAllTimezones())
    .filter(tz => !tz.aliasOf && !tz.name.startsWith('Etc/'))
    .sort((a, b) => a.utcOffset - b.utcOffset)
    .map(tz => ({
        value: tz.name,
        label: `(UTC${tz.utcOffsetStr}) ${tz.name.replace(/_/g, ' ')}`
    }));

/** Match onboarding (BrandingAdvancedTab): Etc/* excluded above; UTC is listed explicitly. */
const TIMEZONE_OPTIONS = [{ value: 'Etc/UTC', label: '(UTC+00:00) UTC' }, ...IANA_TIMEZONES];

const normalizeTimezoneOption = (tz) => {
    if (tz == null || tz === '' || tz === 'UTC') return 'Etc/UTC';
    return tz;
};

const ConfigurationTab = ({ tenantId, token, currentUser }) => {
    const { data: config, isLoading, error, refresh } = useData(`/tenant-onboarding/${tenantId}/full`, token);
    const [saving, setSaving] = useState(null); // 'features', 'quotas', 'support', 'auth', 'ops', 'branding'
    const [branding, setBranding] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState({}); // { logo_light: File, logo_dark: File, ... }
    const [authTimezone, setAuthTimezone] = useState('Etc/UTC');
    const [authDateFormat, setAuthDateFormat] = useState('');
    const [authTimeFormat, setAuthTimeFormat] = useState('');

    const { features, quota, support, auth: authConfig, branding: brandingConfig } = config || {};

    useEffect(() => {
        if (brandingConfig) {
            setBranding(brandingConfig);
        }
    }, [brandingConfig]);

    useEffect(() => {
        setAuthTimezone(normalizeTimezoneOption(authConfig?.timezone));
        setAuthDateFormat(authConfig?.date_format || brandingConfig?.date_format || '');
        setAuthTimeFormat(authConfig?.time_format || brandingConfig?.time_format || '');
    }, [authConfig?.timezone, authConfig?.date_format, authConfig?.time_format, brandingConfig]);

    // Fetch available features dynamically
    const [availableFeatures, setAvailableFeatures] = useState([]);
    useEffect(() => {
        apiFetch("/plans/features", token)
            .then(res => setAvailableFeatures(Array.isArray(res) ? res : []))
            .catch(err => console.error("Failed to load features:", err));
    }, [token]);

    if (isLoading) return <LoadingSpinner />;
    if (error) return <div className="text-red-500">Error loading configuration: {error.message}</div>;
    const isPlatformAdmin = ['r1', 'r2'].includes(currentUser?.roleId);

    // --- SAVE HANDLERS ---

    const saveFeatures = async (newFeatures) => {
        setSaving('features');
        try {
            await apiFetch(`/tenant-onboarding/${tenantId}/features`, token, {
                method: 'POST',
                body: JSON.stringify({
                    enabled_features: newFeatures,
                    api_call_limit: features?.api_call_limit,
                    storage_limit_gb: features?.storage_limit_gb
                })
            });
            notify.success("Feature flags updated.");
            refresh();
        } catch (err) {
            notify.success(err.message);
        } finally {
            setSaving(null);
        }
    };

    const savePlatformOps = async (overrideFeatures) => {
        setSaving('ops');
        try {
            // We use the same features endpoint but specifically for the 'alerts' flag 
            // or other overrides managed by platform admins via PUT /tenants/ features
            await apiFetch(`/tenants/${tenantId}/features`, token, {
                method: 'PUT',
                body: JSON.stringify(overrideFeatures)
            });
            notify.success("Platform operations updated.");
            refresh();
        } catch (err) {
            notify.success(err.message);
        } finally {
            setSaving(null);
        }
    };

    const saveAuth = async (e) => {
        e.preventDefault();
        setSaving('auth');
        const formData = new FormData(e.target);
        const payload = {
            mfa_enabled: formData.get('mfa_enabled') === 'on',
            password_min_length: parseInt(formData.get('password_min_length')) || 8,
            password_expiry_days: parseInt(formData.get('password_expiry_days')) || 90,
            session_expiry_minutes: parseInt(formData.get('session_expiry_minutes')) || 15,
            password_requires_uppercase: formData.get('password_requires_uppercase') === 'on',
            password_requires_lowercase: formData.get('password_requires_lowercase') === 'on',
            password_requires_number: formData.get('password_requires_number') === 'on',
            password_requires_special: formData.get('password_requires_special') === 'on',
            // sso_provider: formData.get('sso_provider') || null,
            // MUI native TextField select does not reliably include name in FormData; use explicit state (same IANA list as onboarding).
            timezone: authTimezone || 'Etc/UTC',
            date_format: authDateFormat || null,
            time_format: authTimeFormat || null,
        };

        try {
            await apiFetch(`/tenant-onboarding/${tenantId}/auth`, token, { method: 'POST', body: JSON.stringify(payload) });
            notify.success("Authentication settings updated.");
            refresh();
        } catch (err) {
            notify.success(err.message);
        } finally {
            setSaving(null);
        }
    };

    const saveQuotas = async (e) => {
        e.preventDefault();
        setSaving('quotas');
        const formData = new FormData(e.target);
        const payload = {
            api_call_limit: parseInt(formData.get('api_call_limit')) || 0,
            user_limit: parseInt(formData.get('user_limit')) || 0,
        };
        try {
            await apiFetch(`/tenant-onboarding/${tenantId}/quotas`, token, {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            notify.success("Resource quotas updated.");
            refresh();
        } catch (err) {
            notify.success(err.message);
        } finally {
            setSaving(null);
        }
    };

    const saveSupport = async (e) => {
        e.preventDefault();
        setSaving('support');
        const formData = new FormData(e.target);
        const payload = {
            support_tier: formData.get('support_tier'),
            sla_description: formData.get('sla_description'),
            response_time_hours: parseInt(formData.get('response_time_hours')) || 24,
        };
        try {
            await apiFetch(`/tenant-onboarding/${tenantId}/support`, token, {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            notify.success("Support configuration updated.");
            refresh();
        } catch (err) {
            notify.success(err.message);
        } finally {
            setSaving(null);
        }
    };

    const saveBranding = async () => {
        setSaving('branding');
        try {
            const formData = new FormData();
            
            // Add JSON configs
            formData.append('theme_config', JSON.stringify(branding.theme_config || {}));
            formData.append('watermark_config', JSON.stringify(branding.watermark_config || {}));
            
            // Add basic fields
            formData.append('custom_css', branding.custom_css || '');
            formData.append('timezone', normalizeTimezoneOption(branding.timezone));
            if (branding.date_format) formData.append('date_format', branding.date_format);
            if (branding.time_format) formData.append('time_format', branding.time_format);

            // Add files if selected
            Object.entries(selectedFiles).forEach(([key, file]) => {
                if (file) formData.append(key, file);
            });

            await updateTenantBranding(tenantId, token, formData);
            notify.success("Branding updated successfully.");
            setSelectedFiles({});
            refresh();
        } catch (err) {
            notify.success(err.message);
        } finally {
            setSaving(null);
        }
    };

    // --- RENDER HELPERS ---

    const toggleFeature = (key) => {
        const current = features?.enabled_features || {};
        const newState = { ...current, [key]: !current[key] };
        saveFeatures(newState);
    };

    const toggleAlerts = () => {
        const current = features?.enabled_features || {};
        // Default is TRUE if missing. So if undefined, toggling means setting to FALSE.
        // If present, toggle value.
        const currentVal = current.alerts !== false; // effectively true
        const newVal = !currentVal;

        savePlatformOps({ alerts: newVal });
    };

    const toggleSingleSession = () => {
        const current = features?.enabled_features || {};
        const currentVal = current.single_session === true;
        savePlatformOps({ single_session: !currentVal });
    };

    // Extra features that must always appear in the list, regardless of what /plans/features returns.
    // These are added to the DB via seed but may be missing in older environments.
    const REQUIRED_FEATURES = [
        { featureId: 'ai_insight', featureName: 'Agentic Insights', featureDescription: 'Ask questions and generate insights from data using natural language' },
    ];

    // Merge: start from API result (or fallback list), then append any REQUIRED_FEATURES not already present
    const baseFeatures = availableFeatures.length > 0
        ? availableFeatures
        : [
            { featureId: 'dashboard', featureName: 'Dashboard Access', featureDescription: 'Allow users to view dashboards' },
            { featureId: 'dashboard_studio', featureName: 'Dashboard Studio', featureDescription: 'Build dashboards' },
            { featureId: 'data_models', featureName: 'Data Models', featureDescription: 'Create unified data models' },
            { featureId: 'data_studio', featureName: 'Data Studio', featureDescription: 'Data engineering & ETL' },
            { featureId: 'data_profiler', featureName: 'Data Profiler', featureDescription: 'Advanced data profiling capabilities' },
            { featureId: 'ml_engine', featureName: 'ML Engine', featureDescription: 'Enable machine learning execution' },
            { featureId: 'ml_studio', featureName: 'ML Studio', featureDescription: 'Create ML models' },
            { featureId: 'genai', featureName: 'GenAI Studio', featureDescription: 'Access to LLM tools' },
            { featureId: 'workflow_studio', featureName: 'Workflow Studio', featureDescription: 'Build and run workflows' },
            { featureId: 'worklist_studio', featureName: 'Worklist Studio', featureDescription: 'Design specialized worklists' },
            { featureId: 'marketplace', featureName: 'App Marketplace', featureDescription: 'Install new apps' },
        ];

    const existingIds = new Set(baseFeatures.map(f => f.code || f.featureId));
    const displayFeatures = [
        ...baseFeatures,
        ...REQUIRED_FEATURES.filter(f => !existingIds.has(f.featureId)),
    ];

    return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 pb-20">
            {/* 1. FEATURES CARD */}
            <ConfigCard title="Feature Flags" icon="ToggleRight">
                <div className="space-y-1">
                    {displayFeatures.map(item => {
                        const id = item.code || item.featureId;
                        const name = item.name || item.featureName;
                        const desc = item.description || item.featureDescription;
                        return (
                            <FeatureToggle
                                key={id}
                                label={name}
                                description={desc}
                                checked={features?.enabled_features?.[id] || false}
                                onChange={() => toggleFeature(id)}
                            />
                        );
                    })}
                </div>
            </ConfigCard>

            {/* 1.5 AUTHENTICATION & SECURITY */}
            <ConfigCard title="Authentication & Security" icon="Lock"
                actions={
                    <Button size="sm" form="authForm" type="submit" isLoading={saving === 'auth'}>
                        Save Auth
                    </Button>
                }
            >
                <div className="mb-6 pb-6 border-b border-gray-100 dark:border-gray-700">
                    <FeatureToggle
                        label="Enforce Single Session"
                        description="If enabled, logging in from a new device will automatically invalidate previous sessions."
                        checked={features?.enabled_features?.single_session === true}
                        onChange={toggleSingleSession}
                    />
                </div>

                <form id="authForm" onSubmit={saveAuth} className="space-y-4">
                    <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-gray-700">
                        <div>
                            <label htmlFor="mfa_enabled" className="block text-sm font-medium text-gray-900 dark:text-gray-100">Multi-Factor Authentication</label>
                            <p className="text-xs text-gray-500">Require all users to setup 2FA.</p>
                        </div>
                        <div className="flex items-center">
                            <input type="checkbox" name="mfa_enabled" id="mfa_enabled" defaultChecked={authConfig?.mfa_enabled} className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary toggle-checkbox" />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormInput label="Min Password Length" name="password_min_length" type="number" defaultValue={authConfig?.password_min_length || 8} />
                        <FormInput label="Password Expiry (Days)" name="password_expiry_days" type="number" defaultValue={authConfig?.password_expiry_days || 90} />
                    </div>

                    <div className="pt-2 pb-2">
                        <p className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Password Requirements</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="flex items-center gap-2">
                                <input type="checkbox" name="password_requires_uppercase" id="password_requires_uppercase" defaultChecked={authConfig?.password_requires_uppercase !== false} className="rounded border-gray-300 text-primary focus:ring-primary" />
                                <label htmlFor="password_requires_uppercase" className="text-sm text-gray-700 dark:text-gray-300">Uppercase</label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input type="checkbox" name="password_requires_lowercase" id="password_requires_lowercase" defaultChecked={authConfig?.password_requires_lowercase !== false} className="rounded border-gray-300 text-primary focus:ring-primary" />
                                <label htmlFor="password_requires_lowercase" className="text-sm text-gray-700 dark:text-gray-300">Lowercase</label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input type="checkbox" name="password_requires_number" id="password_requires_number" defaultChecked={authConfig?.password_requires_number !== false} className="rounded border-gray-300 text-primary focus:ring-primary" />
                                <label htmlFor="password_requires_number" className="text-sm text-gray-700 dark:text-gray-300">Number</label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input type="checkbox" name="password_requires_special" id="password_requires_special" defaultChecked={authConfig?.password_requires_special !== false} className="rounded border-gray-300 text-primary focus:ring-primary" />
                                <label htmlFor="password_requires_special" className="text-sm text-gray-700 dark:text-gray-300">Special Char</label>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormInput label="Session Expiry (Minutes)" name="session_expiry_minutes" type="number" defaultValue={authConfig?.session_expiry_minutes || 15} />
                    </div>

                    {/* <FormSelect label="SSO Provider" name="sso_provider" defaultValue={authConfig?.sso_provider || ""}>
                        <option value="">None (Local Auth)</option>
                        <option value="google">Google Workspace</option>
                        <option value="azure">Azure AD / Entra ID</option>
                        <option value="okta">Okta</option>
                    </FormSelect> */}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormSelect
                            label="Timezone"
                            name="timezone"
                            value={authTimezone}
                            onChange={(e) => setAuthTimezone(e.target.value)}
                        >
                            {TIMEZONE_OPTIONS.map(tz => (
                                <option key={tz.value} value={tz.value}>
                                    {tz.label}
                                </option>
                            ))}
                        </FormSelect>
                        
                        <FormSelect
                            label="Date Format"
                            name="date_format"
                            value={authDateFormat}
                            onChange={(e) => setAuthDateFormat(e.target.value)}
                        >
                            <option value="">System Default (YYYY-MM-DD)</option>
                            <option value="DD/MM/YYYY">DD/MM/YYYY (e.g. 31/12/2026)</option>
                            <option value="MM/DD/YYYY">MM/DD/YYYY (e.g. 12/31/2026)</option>
                            <option value="YYYY-MM-DD">YYYY-MM-DD (e.g. 2026-12-31)</option>
                            <option value="DD MMM YYYY">DD MMM YYYY (e.g. 31 Dec 2026)</option>
                            <option value="MMM DD, YYYY">MMM DD, YYYY (e.g. Dec 31, 2026)</option>
                        </FormSelect>

                        <FormSelect
                            label="Time Format"
                            name="time_format"
                            value={authTimeFormat}
                            onChange={(e) => setAuthTimeFormat(e.target.value)}
                        >
                            <option value="">System Default</option>
                            <option value="HH:mm:ss">24-hour (e.g. 14:30:00)</option>
                            <option value="hh:mm:ss A">12-hour (e.g. 02:30:00 PM)</option>
                        </FormSelect>
                    </div>
                </form>
            </ConfigCard>

            {/* 2. QUOTAS CARD */}
            <ConfigCard title="Resource Quotas" icon="BarChart3"
                actions={
                    <Button size="sm" form="quotaForm" type="submit" isLoading={saving === 'quotas'}>
                        Save Quotas
                    </Button>
                }
            >
                <form id="quotaForm" onSubmit={saveQuotas} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput label="Monthly Credits" name="api_call_limit" type="number" defaultValue={quota?.api_call_limit} placeholder="e.g. 50000" />
                    <FormInput label="Max Users (Concurrent)" name="user_limit" type="number" defaultValue={quota?.user_limit} placeholder="e.g. 25" />
                </form>
            </ConfigCard>

            {/* 3. SUPPORT CARD */}
            <ConfigCard title="Support & SLA" icon="LifeBuoy"
                actions={
                    <Button size="sm" form="supportForm" type="submit" isLoading={saving === 'support'}>
                        Update Support
                    </Button>
                }
            >
                <form id="supportForm" onSubmit={saveSupport} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormSelect label="Support Tier" name="support_tier" defaultValue={support?.support_tier}>
                            <option value="Basic">Basic</option>
                            <option value="Silver">Silver</option>
                            <option value="Gold">Gold</option>
                            <option value="Platinum">Platinum</option>
                        </FormSelect>
                        <FormInput label="Response Time (Hours)" name="response_time_hours" type="number" defaultValue={support?.response_time_hours} />
                    </div>
                    <FormInput label="SLA Description" name="sla_description" defaultValue={support?.sla_description} />
                </form>
            </ConfigCard>

            {/* 5. BRANDING & APPEARANCE */}
            {branding && (
                <ConfigCard 
                    title="Branding & Appearance" 
                    icon="Palette"
                    actions={
                        <Button size="sm" onClick={saveBranding} isLoading={saving === 'branding'}>
                            Save Branding
                        </Button>
                    }
                >
                    <div className="space-y-8">
                        {/* Assets Row */}
                        <div className="max-w-3xl">
                            <div className="grid grid-cols-3 gap-8">
                                {[
                                    { key: 'logo_light', label: 'LIGHT LOGO', urlKey: 'logo_light_url', hint: '140x80px' },
                                    { key: 'logo_dark', label: 'DARK LOGO', urlKey: 'logo_dark_url', hint: '140x80px' },
                                    { key: 'collapsed_logo', label: 'COLLAPSED', urlKey: 'collapsed_logo_url', hint: '80x80px' }
                                ].map((field) => (
                                    <div key={field.key} className="space-y-1.5">
                                        <div>
                                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-tighter leading-tight">
                                                {field.label}
                                            </label>
                                        </div>
                                        <div 
                                            className="relative aspect-video md:aspect-square bg-gray-50 dark:bg-gray-900 rounded border border-dashed border-gray-300 dark:border-gray-700 flex items-center justify-center group cursor-pointer hover:border-primary/50 transition-colors"
                                            onClick={() => document.getElementById(`file-${field.key}`).click()}
                                        >
                                            {selectedFiles[field.key] ? (
                                                <img src={URL.createObjectURL(selectedFiles[field.key])} alt="Preview" className="max-w-full max-h-full object-contain p-2 rounded" />
                                            ) : branding[field.urlKey] ? (
                                                <img src={branding[field.urlKey]} alt="Current" className="max-w-full max-h-full object-contain p-2 rounded" />
                                            ) : (
                                                <div className="flex flex-col items-center gap-1 text-center p-4">
                                                    <DynamicIcon name="Upload" className="w-5 h-5 text-gray-300 group-hover:text-primary transition-colors mb-1" />
                                                    <p className="text-[10px] font-semibold text-gray-500 group-hover:text-primary transition-colors">
                                                        Click or drag
                                                    </p>
                                                    <p className="text-[9px] text-gray-400 opacity-70">
                                                        {field.hint} (Max 2MB)
                                                    </p>
                                                </div>
                                            )}
                                            
                                            <input 
                                                type="file" 
                                                id={`file-${field.key}`}
                                                className="hidden"
                                                accept={field.key === 'favicon' ? ".ico,.png" : ".png,.jpg,.jpeg,.svg"}
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) setSelectedFiles(prev => ({ ...prev, [field.key]: file }));
                                                }}
                                            />

                                            {(selectedFiles[field.key] || branding[field.urlKey]) && (
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (selectedFiles[field.key]) {
                                                            setSelectedFiles(prev => {
                                                                const n = { ...prev };
                                                                delete n[field.key];
                                                                return n;
                                                            });
                                                        } else {
                                                            setBranding(prev => ({ ...prev, [field.urlKey]: null }));
                                                        }
                                                    }}
                                                    className="absolute -top-2 -right-2 p-1.5 bg-red-50 text-red-500 rounded-full shadow-md hover:bg-red-100 opacity-0 group-hover:opacity-100 transition-all z-10 border border-red-100"
                                                    title="Remove image"
                                                >
                                                    <DynamicIcon name="Trash2" className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Theme & Styles */}
                        <div className="space-y-4 pt-6 border-t border-gray-100 dark:border-gray-700">
                            <div className="flex items-center gap-2 mb-2">
                                <DynamicIcon name="Palette" className="w-4 h-4 text-gray-400" />
                                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">Theme & Styles</h4>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                                {/* Primary Color */}
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-gray-500">Primary Color</label>
                                    <div className="flex items-center gap-2">
                                        <input 
                                            type="color" 
                                            value={branding.theme_config?.theme_color || '#3b82f6'}
                                            onChange={(e) => {
                                                const color = e.target.value;
                                                const secondary = generateSecondaryColor(color);
                                                setBranding({ 
                                                    ...branding, 
                                                    theme_config: { ...branding.theme_config, theme_color: color, secondary_color: secondary } 
                                                });
                                            }}
                                            className="w-10 h-10 p-0 border-0 bg-transparent cursor-pointer rounded overflow-hidden"
                                        />
                                        <input 
                                            type="text"
                                            value={branding.theme_config?.theme_color || '#3b82f6'}
                                            onChange={(e) => {
                                                const color = e.target.value;
                                                const secondary = generateSecondaryColor(color);
                                                setBranding({ 
                                                    ...branding, 
                                                    theme_config: { ...branding.theme_config, theme_color: color, secondary_color: secondary } 
                                                });
                                            }}
                                            className="flex-grow px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 focus:ring-1 focus:ring-primary outline-none"
                                        />
                                    </div>
                                </div>

                                {/* Secondary Color */}
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-gray-500">Secondary (Calculated)</label>
                                    <div className="flex items-center gap-2">
                                        <input 
                                            type="color" 
                                            value={branding.theme_config?.secondary_color || '#F9F1F1'}
                                            onChange={(e) => setBranding({ 
                                                ...branding, 
                                                theme_config: { ...branding.theme_config, secondary_color: e.target.value } 
                                            })}
                                            className="w-10 h-10 p-0 border-0 bg-transparent cursor-pointer rounded overflow-hidden"
                                        />
                                        <input 
                                            type="text"
                                            value={branding.theme_config?.secondary_color || '#F9F1F1'}
                                            onChange={(e) => setBranding({ 
                                                ...branding, 
                                                theme_config: { ...branding.theme_config, secondary_color: e.target.value } 
                                            })}
                                            className="flex-grow px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 focus:ring-1 focus:ring-primary outline-none"
                                        />
                                    </div>
                                    <div className="pt-2">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-[10px] text-gray-500 font-medium">Refine Secondary Shade</span>
                                        </div>
                                        <input 
                                            type="range" 
                                            min="-40" 
                                            max="40" 
                                            defaultValue="0"
                                            onChange={(e) => {
                                                const offset = parseInt(e.target.value);
                                                const primary = branding.theme_config?.theme_color || '#3b82f6';
                                                const newSecondary = generateSecondaryColorWithOffset(primary, offset);
                                                setBranding({ 
                                                    ...branding, 
                                                    theme_config: { ...branding.theme_config, secondary_color: newSecondary } 
                                                });
                                            }}
                                            className="w-full h-1 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary"
                                        />
                                        <p className="text-[10px] text-gray-400 italic mt-1">Adjust darkness based on primary color.</p>
                                    </div>
                                </div>

                                {/* Border Radius */}
                                <div className="space-y-1.5">
                                    <div className="flex justify-between items-center">
                                        <label className="text-xs font-medium text-gray-500">Border Radius ({branding.theme_config?.border_radius ?? 4}px)</label>
                                    </div>
                                    <input 
                                        type="range" 
                                        min="0" 
                                        max="16" 
                                        value={branding.theme_config?.border_radius ?? 4}
                                        onChange={(e) => setBranding({ 
                                            ...branding, 
                                            theme_config: { ...branding.theme_config, border_radius: parseInt(e.target.value) } 
                                        })}
                                        className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary mt-3"
                                    />
                                    <div className="flex gap-4 pt-2">
                                        <button 
                                            className="px-4 py-2 bg-primary/10 text-primary text-xs font-semibold border border-primary/20"
                                            style={{ borderRadius: `${branding.theme_config?.border_radius ?? 4}px` }}
                                        >
                                            Solid Button
                                        </button>
                                        <div 
                                            className="px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-400 text-xs font-medium flex items-center justify-center min-w-[80px]"
                                            style={{ borderRadius: `${branding.theme_config?.border_radius ?? 4}px` }}
                                        >
                                            Input field
                                        </div>
                                    </div>
                                </div>

                                {/* Font Family */}
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-gray-500">Font Family</label>
                                    <FormSelect 
                                        value={branding.theme_config?.font_family || 'Inter'}
                                        onChange={(e) => setBranding({ 
                                            ...branding, 
                                            theme_config: { ...branding.theme_config, font_family: e.target.value } 
                                        })}
                                    >
                                        {FONT_FAMILIES.map(font => (
                                            <option key={font} value={font}>{font}</option>
                                        ))}
                                    </FormSelect>
                                </div>
                            </div>
                        </div>

                        {/* Dashboard Watermark */}
                        <div className="space-y-4 pt-6 border-t border-gray-100 dark:border-gray-700">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <DynamicIcon name="Layers" className="w-4 h-4 text-gray-400" />
                                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">Dashboard Watermark</h4>
                                </div>
                                <div 
                                    className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors ${branding.watermark_config?.watermark_enabled ? 'bg-primary' : 'bg-gray-300'}`}
                                    onClick={() => setBranding({
                                        ...branding,
                                        watermark_config: { ...branding.watermark_config, watermark_enabled: !branding.watermark_config?.watermark_enabled }
                                    })}
                                >
                                    <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${branding.watermark_config?.watermark_enabled ? 'right-1' : 'left-1'}`} />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                                <FormInput 
                                    value={branding.watermark_config?.watermark_text || ''}
                                    onChange={(e) => setBranding({
                                        ...branding,
                                        watermark_config: { ...branding.watermark_config, watermark_text: e.target.value }
                                    })}
                                    placeholder="Watermark Text"
                                />
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Opacity ({branding.watermark_config?.watermark_opacity || 30}%)</label>
                                    <input 
                                        type="range" 
                                        min="0" 
                                        max="100" 
                                        value={branding.watermark_config?.watermark_opacity || 30}
                                        onChange={(e) => setBranding({
                                            ...branding,
                                            watermark_config: { ...branding.watermark_config, watermark_opacity: parseInt(e.target.value) }
                                        })}
                                        className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                                    />
                                </div>
                            </div>
                            <p className="text-[11px] italic text-gray-400 mt-1">* Watermark is only visible on dashboards and exported reports.</p>
                        </div>
                    </div>
                </ConfigCard>
            )}
        </div>
    );
};

export default ConfigurationTab;
